import React, { useEffect, useState, useRef, useMemo } from 'react';
import useAddressValidation from "../Hooks/useAddressValidation";
import useTask from '../Hooks/useTask';
import ComponentFactory from '../Components/Factory/ComponentFactory';
import ModalComponent from '../Components/ModalComponent';
import localConfig from '../localData/localConfig.json';
import { useLanguage } from '../Context/LanguageContext';
import { loadTranslationBundle } from '../Services/translationLoader';
import { applyTranslation } from '../Utils/translationOverlay';
import { isErrorDetailsEnabled } from '../Utils/telemetry';

import { Box, SimpleGrid, Card, CardBody, CardHeader, Flex, Text, Button, FormControl, FormLabel, Select, VStack, Heading, useColorModeValue, Input, FormErrorMessage, Skeleton, SkeletonText, ButtonGroup, Portal, useDisclosure, InputGroup, InputLeftElement, InputRightElement, IconButton, List, ListItem, HStack, Badge, Image, Slider, SliderTrack, SliderFilledTrack, SliderThumb, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Alert, AlertIcon, AlertDescription, Spinner, Checkbox, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon } from '@chakra-ui/react';
import NavBarComponent from '../Components/NavBarComponent';
import ButtonComponent from '../Components/ButtonComponent';
import DOMPurify from 'dompurify';
import appConfig from '../Config/appConfig';
import PageViewTracker from '../Components/PageViewTracker';
import api from '../Api/api';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { SearchIcon, ChevronDownIcon, CloseIcon, CheckIcon } from '@chakra-ui/icons';
import * as FiIcons from 'react-icons/fi';
import loadingHeart from '../loadingHeart.gif';

// Custom Searchable Dropdown Component
const SearchableTaskDropdown = ({
  value,
  onChange,
  categories,
  placeholder = "Search tasks...",
  isRequired = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [keyboardIndex, setKeyboardIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // All tasks flattened — used for search + keyboard navigation
  const allTasks = useMemo(() =>
    (categories || []).flatMap(category =>
      (category.options || []).map(task => ({
        ...task,
        categoryName: category.name,
        categoryIcon: category.icon,
      }))
    ), [categories]);

  // Filtered flat list (search mode only)
  const filteredFlat = useMemo(() => {
    if (!searchQuery.trim()) return allTasks;
    const q = searchQuery.toLowerCase().trim();
    return allTasks.filter(task =>
      task.label?.toLowerCase().includes(q) ||
      task.value?.toLowerCase().includes(q) ||
      task.categoryName?.toLowerCase().includes(q)
    );
  }, [allTasks, searchQuery]);

  // Selected task info
  const selectedTask = useMemo(() =>
    allTasks.find(t => t.value === value), [allTasks, value]);

  const close = () => {
    setIsOpen(false);
    setSearchQuery('');
    setKeyboardIndex(-1);
  };

  const handleSelect = (taskValue) => {
    onChange(taskValue);
    close();
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setKeyboardIndex(0);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setKeyboardIndex(prev => Math.min(prev + 1, filteredFlat.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setKeyboardIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (keyboardIndex >= 0 && filteredFlat[keyboardIndex]) {
          handleSelect(filteredFlat[keyboardIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      default:
        break;
    }
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        close();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isSearchMode = searchQuery.trim().length > 0;

  return (
    <Box ref={dropdownRef} position="relative" width="100%">

      {/* ── Trigger ── */}
      {!isOpen && selectedTask ? (
        /* Selected-state pill — shows icon, name, category badge, monospace value */
        <HStack
          px={4} py={3}
          bg="white"
          border="1.5px solid"
          borderColor="rgba(139,92,246,0.4)"
          borderRadius="xl"
          cursor="pointer"
          boxShadow="0 2px 12px rgba(139,92,246,0.1)"
          _hover={{ borderColor: "purple.400", boxShadow: "0 4px 20px rgba(139,92,246,0.18)", transform: "translateY(-1px)" }}
          transition="all 0.2s ease"
          justify="space-between"
          onClick={() => { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 30); }}
        >
          <HStack spacing={3} flex={1} minW={0}>
            <Box
              p={2} borderRadius="lg" flexShrink={0}
              bgGradient="linear(135deg, purple.500, purple.700)"
            >
              {selectedTask.categoryIcon && FiIcons[`Fi${selectedTask.categoryIcon}`]
                ? React.createElement(FiIcons[`Fi${selectedTask.categoryIcon}`], { size: 15, color: 'white' })
                : <Box w="15px" h="15px" />
              }
            </Box>
            <Box flex={1} minW={0}>
              <Text fontWeight="600" color="gray.800" fontSize="sm" noOfLines={1}>{selectedTask.label}</Text>
              <HStack spacing={2} mt={0.5}>
                <Badge colorScheme="purple" variant="subtle" fontSize="9px" borderRadius="full" px={2}>
                  {selectedTask.categoryName}
                </Badge>
                <Text fontSize="10px" color="gray.400" fontFamily="mono" noOfLines={1}>{selectedTask.value}</Text>
              </HStack>
            </Box>
          </HStack>
          <HStack spacing={1} flexShrink={0}>
            <IconButton
              icon={<CloseIcon boxSize={2} />}
              size="xs" variant="ghost" borderRadius="full"
              color="gray.400" _hover={{ color: "red.400", bg: "red.50" }}
              aria-label="Clear selection"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
            />
            <ChevronDownIcon color="gray.400" />
          </HStack>
        </HStack>
      ) : (
        /* Search input */
        <InputGroup>
          <InputLeftElement pointerEvents="none" h="full" pl={1}>
            <SearchIcon color={isOpen ? "purple.400" : "gray.400"} boxSize={4} />
          </InputLeftElement>
          <Input
            ref={inputRef}
            value={isOpen ? searchQuery : ''}
            onChange={(e) => { setSearchQuery(e.target.value); setKeyboardIndex(0); }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            size="lg"
            bg="white"
            border="1.5px solid"
            borderColor={isOpen ? "purple.400" : "rgba(139,92,246,0.25)"}
            borderRadius="xl"
            boxShadow={isOpen
              ? "0 0 0 3px rgba(139,92,246,0.15), 0 4px 20px rgba(139,92,246,0.08)"
              : "0 2px 8px rgba(139,92,246,0.06)"
            }
            _hover={{ borderColor: "purple.300", boxShadow: "0 4px 16px rgba(139,92,246,0.1)" }}
            _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 3px rgba(139,92,246,0.15)" }}
            _placeholder={{ color: "gray.400", fontWeight: "400" }}
            transition="all 0.2s"
            pl="44px"
            pr="44px"
          />
          <InputRightElement h="full">
            <ChevronDownIcon
              color="gray.400"
              transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
              transition="transform 0.2s"
            />
          </InputRightElement>
        </InputGroup>
      )}

      {/* ── Dropdown panel ── */}
      {isOpen && (
        <Portal>
          {/* Backdrop */}
          <Box
            position="fixed" top="0" left="0" right="0" bottom="0"
            zIndex={1400}
            onClick={close}
          />
          {/* Panel */}
          <Box
            position="fixed"
            top={dropdownRef.current ? `${dropdownRef.current.getBoundingClientRect().bottom + 6}px` : "auto"}
            left={dropdownRef.current ? `${dropdownRef.current.getBoundingClientRect().left}px` : "auto"}
            width={dropdownRef.current ? `${dropdownRef.current.offsetWidth}px` : "auto"}
            maxH="460px"
            overflowY="auto"
            bg="white"
            border="1.5px solid"
            borderColor="rgba(139,92,246,0.18)"
            borderRadius="xl"
            boxShadow="0 24px 64px rgba(139,92,246,0.18), 0 8px 24px rgba(0,0,0,0.08)"
            zIndex={1401}
            onClick={(e) => e.stopPropagation()}
            sx={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(139,92,246,0.3) transparent',
              '&::-webkit-scrollbar': { width: '5px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(139,92,246,0.3)', borderRadius: '3px' },
            }}
          >
            {isSearchMode ? (
              /* ── Search results mode (flat) ── */
              filteredFlat.length === 0 ? (
                <Box py={10} textAlign="center">
                  <Text fontSize="sm" color="gray.400">No tasks match "{searchQuery}"</Text>
                  <Text fontSize="xs" color="gray.300" mt={1}>Try a different search term</Text>
                </Box>
              ) : (
                <Box>
                  <Box px={4} py={2} bg="gray.50" borderBottom="1px solid" borderColor="gray.100">
                    <Text fontSize="xs" color="gray.400" fontWeight="500">
                      {filteredFlat.length} result{filteredFlat.length !== 1 ? 's' : ''} for "{searchQuery}"
                    </Text>
                  </Box>
                  {filteredFlat.map((task, index) => {
                    const isHighlighted = keyboardIndex === index;
                    const isSelected = value === task.value;
                    return (
                      <Box
                        key={task.value}
                        px={4} py={3}
                        cursor="pointer"
                        bg={isSelected ? "rgba(139,92,246,0.06)" : isHighlighted ? "rgba(139,92,246,0.04)" : "white"}
                        borderBottom="1px solid" borderColor="gray.50"
                        _hover={{ bg: "rgba(139,92,246,0.05)" }}
                        _last={{ borderBottom: "none" }}
                        onMouseDown={(e) => { e.preventDefault(); handleSelect(task.value); }}
                        display="flex" alignItems="center" justifyContent="space-between"
                      >
                        <HStack spacing={3} flex={1} minW={0}>
                          <Box p={1.5} borderRadius="md" flexShrink={0}
                            bg={isSelected ? "rgba(139,92,246,0.15)" : "gray.100"}
                          >
                            {task.categoryIcon && FiIcons[`Fi${task.categoryIcon}`]
                              ? React.createElement(FiIcons[`Fi${task.categoryIcon}`], { size: 13, color: isSelected ? '#7c3aed' : '#9ca3af' })
                              : <Box w="13px" h="13px" />
                            }
                          </Box>
                          <Box flex={1} minW={0}>
                            <Text fontWeight={isSelected ? "600" : "500"} fontSize="sm"
                              color={isSelected ? "purple.700" : "gray.700"} noOfLines={1}>
                              {task.label}
                            </Text>
                            <Text fontSize="10px" color="gray.400" fontFamily="mono" noOfLines={1}>{task.value}</Text>
                          </Box>
                        </HStack>
                        <HStack spacing={2} flexShrink={0}>
                          <Badge colorScheme="purple" variant="subtle" fontSize="9px" borderRadius="full">
                            {task.categoryName}
                          </Badge>
                          {isSelected && <CheckIcon color="purple.500" boxSize={3} />}
                        </HStack>
                      </Box>
                    );
                  })}
                </Box>
              )
            ) : (
              /* ── Browse mode — grouped by category ── */
              <Box>
                {(categories || []).map((category, catIndex) => {
                  const catTasks = category.options || [];
                  if (catTasks.length === 0) return null;
                  const CatIcon = category.icon ? FiIcons[`Fi${category.icon}`] : null;
                  return (
                    <Box key={catIndex}>
                      {/* Category header */}
                      <Box
                        px={4} py={2.5}
                        bg="rgba(139,92,246,0.04)"
                        borderBottom="1px solid" borderColor="rgba(139,92,246,0.1)"
                        borderTop={catIndex > 0 ? "2px solid" : "none"}
                        borderTopColor="rgba(139,92,246,0.07)"
                        position="sticky" top="0" zIndex={1}
                      >
                        <HStack spacing={2}>
                          {CatIcon && (
                            <Box color="purple.500">
                              {React.createElement(CatIcon, { size: 12 })}
                            </Box>
                          )}
                          <Text
                            fontSize="10px" fontWeight="700"
                            color="purple.600"
                            textTransform="uppercase"
                            letterSpacing="0.1em"
                          >
                            {category.name}
                          </Text>
                          <Badge
                            ml="auto" colorScheme="purple" variant="subtle"
                            fontSize="9px" borderRadius="full"
                          >
                            {catTasks.length}
                          </Badge>
                        </HStack>
                      </Box>
                      {/* Tasks under this category */}
                      {catTasks.map((task) => {
                        const isSelected = value === task.value;
                        return (
                          <Box
                            key={task.value}
                            pl={10} pr={4} py={3}
                            cursor="pointer"
                            bg={isSelected ? "rgba(139,92,246,0.06)" : "white"}
                            borderBottom="1px solid" borderColor="gray.50"
                            _hover={{ bg: "rgba(139,92,246,0.04)" }}
                            _last={{ borderBottom: "none" }}
                            onMouseDown={(e) => { e.preventDefault(); handleSelect(task.value); }}
                            display="flex" alignItems="center" justifyContent="space-between"
                          >
                            <Box flex={1} minW={0}>
                              <Text
                                fontWeight={isSelected ? "600" : "400"}
                                fontSize="sm"
                                color={isSelected ? "purple.700" : "gray.700"}
                                noOfLines={1}
                              >
                                {task.label}
                              </Text>
                              <Text fontSize="10px" color="gray.400" fontFamily="mono" noOfLines={1}>
                                {task.value}
                              </Text>
                            </Box>
                            {isSelected && <CheckIcon color="purple.500" boxSize={3} ml={2} flexShrink={0} />}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Portal>
      )}
    </Box>
  );
};

const parseLaunchContextParams = (search) => {
  const rawParams = new URLSearchParams(search || '');
  const parsedParams = {};

  rawParams.forEach((value, key) => {
    if (value !== undefined && value !== null && value !== '') {
      parsedParams[key] = value;
    }
  });

  const decodedSearch = decodeURIComponent((search || '').replace(/^\?/, ''));
  if (!decodedSearch.includes('<6><5>')) {
    return parsedParams;
  }

  decodedSearch.split('<6><5>').forEach((part) => {
    const separatorIndex = part.indexOf(':');
    if (separatorIndex <= 0) return;

    const key = part.slice(0, separatorIndex).trim();
    const value = part.slice(separatorIndex + 1).trim();

    if (key && value !== '') {
      parsedParams[key] = value;
    }
  });

  return parsedParams;
};

const inferFirstNameFromEnglishText = (rawText) => {
  if (!rawText || typeof rawText !== 'string') return '';
  // No ^ anchor so it matches anywhere in the string — needed when scanning JSON.stringify'd sections
  // Exclude JSON delimiters (" \) from capture group to avoid spilling into surrounding JSON
  const match = rawText.match(/#{0,6}\s*(?:Hi|Hello)\s*,\s*([^!,.\n"\\]+)/i);
  return match?.[1]?.trim() || '';
};

const inferFirstNameFromEnglishContent = (rawTitle, rawSections) => {
  const fromTitle = inferFirstNameFromEnglishText(rawTitle);
  if (fromTitle) return fromTitle;

  if (!Array.isArray(rawSections) || rawSections.length === 0) return '';

  for (const section of rawSections) {
    const fromSection = inferFirstNameFromEnglishText(JSON.stringify(section));
    if (fromSection) return fromSection;
  }

  return '';
};

const ContentPage = () => {
  const [showNavBar, setShowNavBar] = useState(true);
  const [loadingTimer, setLoadingTimer] = useState(null);
  const [delayedTaskLoading, setDelayedTaskLoading] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [mockData, setMockData] = useState(null);
  const [config, setConfig] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  
  const location = useLocation(); 
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const launchContextParams = useMemo(() => parseLaunchContextParams(location.search), [location.search]);
  const getParamValue = (key) => {
    const queryValue = params.get(key);
    if (queryValue !== null && queryValue !== undefined && queryValue !== '') {
      return queryValue;
    }
    return launchContextParams[key];
  };
  const taskInst = getParamValue('taskInst');

  const [formData, setFormData] = useState({});
  const [dynamicLongContent, setDynamicLongContent] = useState(false);
  const [dynamicValidationState, setDynamicValidationState] = useState({});
  const [currentStepHasRequiredFields, setCurrentStepHasRequiredFields] = useState(false);
  const [mocke8ScoreData, setMocke8ScoreData] = useState({});
  const isUpdatingUrl = useRef(false);

  // ── Clone Task modal state ───────────────────────────────────────────────────
  const { isOpen: isCloneOpen, onOpen: onCloneOpen, onClose: onCloneClose } = useDisclosure();
  const [cloneNewTaskCode, setCloneNewTaskCode] = useState('');
  const [cloneRevision, setCloneRevision] = useState('Latest');
  const [cloneUpdateConfig, setCloneUpdateConfig] = useState(true);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [cloneResult, setCloneResult] = useState(null); // { success, error }
  const [errorDumpCopied, setErrorDumpCopied] = useState(false);

  const handleOpenCloneModal = () => {
    setCloneNewTaskCode('');
    setCloneRevision('Latest');
    setCloneUpdateConfig(true);
    setCloneLoading(false);
    setCloneResult(null);
    onCloneOpen();
  };

  const handleCloneTask = async () => {
    setCloneLoading(true);
    setCloneResult(null);
    try {
      const response = await api.post(`${appConfig.APIBaseURL}/api/v1/content/clone`, {
        sourceTaskCode: formData.taskRefId,
        sourceRevision: cloneRevision.trim() || 'Latest',
        newTaskCode: cloneNewTaskCode.trim(),
        updateCustomerConfig: cloneUpdateConfig,
      });
      setCloneResult({ success: true });
      setTimeout(() => {
        onCloneClose();
        window.location.href = `${location.pathname}?taskRefId=${encodeURIComponent(cloneNewTaskCode.trim())}`;
      }, 2000);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Clone failed.';
      setCloneResult({ success: false, error: msg });
    } finally {
      setCloneLoading(false);
    }
  };

  // ── Language / Translation ───────────────────────────────────────────────────
  const { locale, setLanguage, SUPPORTED_LOCALES } = useLanguage();
  const [translationBundle, setTranslationBundle] = useState(null);

  // Function to check if current step has required fields
  const checkCurrentStepRequirements = () => {
    if (!sections || sections.length === 0) {
      setCurrentStepHasRequiredFields(false);
      return;
    }

    // Check if any section has required fields
    const hasRequiredFields = sections.some(section => {
      if (section.type === 'questionnaire' && section.questions) {
        return section.questions.some(question => question.required);
      }
      if (section.type === 'form' && section.fields) {
        return section.fields.some(field => field.required);
      }
      if (section.type === 'form' && section.required && Array.isArray(section.required)) {
        return section.required.length > 0;
      }
      if (section.type === 'form' && section.items) {
        return section.items.some(item => item.required);
      }
      return false;
    });

    setCurrentStepHasRequiredFields(hasRequiredFields);
  };

  // Simple validation to check if required fields are filled
  const isFormValid = (formData, sliderData = {}) => {
    if (!config?.workflowConfiguration) return false;

    // Task reference is always required
    if (!formData.taskRefId) return false;

    const selectedTaskRef = getSelectedTaskReference(formData.taskRefId);

    if (!selectedTaskRef?.metadata) return true; // No metadata means no additional requirements

    const requiredFields = getTaskRefRequiredFields(selectedTaskRef);

    // Validate each required field
    for (const fieldName of requiredFields) {
      let fieldValue = formData[fieldName];
      
      // Special handling for slider fields - check sliderData
      if (fieldName === 'mocke8ScoreData' && config.workflowConfiguration[fieldName]?.type === 'slider') {
        const sliderConfig = config.workflowConfiguration[fieldName];
        if (sliderConfig.e8Sliders) {
          // Check if all sliders have values
          const hasAllSliderValues = sliderConfig.e8Sliders.every(slider => 
            sliderData[slider.key] !== undefined && sliderData[slider.key] !== null
          );
          if (!hasAllSliderValues) {
            return false;
          }
        }
        continue; // Skip normal validation for slider fields
      }
      
      if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
        return false;
      }
      
      // Check for field-specific validation rules from task metadata
      const taskValidationRules = selectedTaskRef.metadata.validationRules?.[fieldName];
      if (taskValidationRules) {
        // Check minCount for arrays
        if (taskValidationRules.minCount && Array.isArray(fieldValue)) {
          if (fieldValue.length < taskValidationRules.minCount) {
            return false;
          }
        }
        
        // Check required flag
        if (taskValidationRules.required && (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0))) {
          return false;
        }
      }
    }

    return true;
  };

  const handleInputChange = (name, value) => {
    
    setFormData((prev) => {
      const newFormData = { ...prev, [name]: value };
      
             // Log debug info when taskRefId changes
       if (name === 'taskRefId' && value) {
         const selectedTaskRef = getSelectedTaskReference(value);

                                       if (selectedTaskRef?.metadata) {
            const requiredFields = getTaskRefRequiredFields(selectedTaskRef);
          }
       }
      
      // Handle platform-specific logic
      if (name === 'platform' && config?.workflowConfiguration?.platform?.options) {
        const selectedPlatform = config.workflowConfiguration.platform.options.find(
          (opt) => opt.value === value
        );
        newFormData.SSLaunch = selectedPlatform ? String(selectedPlatform.sslLaunch) : '0';
      }
      
      // Update URL immediately when form fields change
      if (config?.workflowConfiguration && Object.keys(config.workflowConfiguration).includes(name)) {
        // Always update URL for any configuration field
        const shouldUpdate = newFormData[name] !== undefined;
        
        if (shouldUpdate) {
          const newParams = new URLSearchParams(location.search);
          
          // Set the parameter directly (no mapping needed)
          newParams.set(name, value);
          
          // Also update other form fields in URL
          Object.keys(newFormData).forEach((key) => {
            if (key !== name && newFormData[key] !== undefined && newFormData[key] !== '') {
              if (Array.isArray(newFormData[key])) {
                if (newFormData[key].length > 0) {
                  newParams.set(key, newFormData[key].join(','));
                }
              } else {
                newParams.set(key, newFormData[key]);
              }
            }
          });
          
          // Only navigate if the URL actually changed
          const currentUrl = location.search;
          const newUrl = newParams.toString();
          if (currentUrl !== `?${newUrl}`) {
            isUpdatingUrl.current = true;
            navigate(`${location.pathname}?${newUrl}`, { replace: true });
            // Reset the flag after a short delay
            setTimeout(() => {
              isUpdatingUrl.current = false;
            }, 100);
          }
        }
      }
      
      // Special handling for taskRefId - mirror to taskReference and update URL immediately
      if (name === 'taskRefId') {
        // Keep taskReference in sync for compatibility
        newFormData.taskReference = value;
        const newParams = new URLSearchParams(location.search);
        newParams.set('taskRefId', value);
        newParams.set('taskReference', value);
        
        // Clear other form fields when taskRefId changes (since they might not be relevant for the new task)
        Object.keys(newFormData).forEach((key) => {
          if (key !== 'taskRefId' && key !== name) {
            newParams.delete(key);
          }
        });
        
        const currentUrl = location.search;
        const newUrl = newParams.toString();
        if (currentUrl !== `?${newUrl}`) {
          isUpdatingUrl.current = true;
          navigate(`${location.pathname}?${newUrl}`, { replace: true });
          // Reset the flag after a short delay
          setTimeout(() => {
            isUpdatingUrl.current = false;
          }, 100);
        }
      }
      
       return newFormData;
    });
  };

  const formatLabel = (key) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const getSelectedTaskReference = (taskRefId) => {
    if (!taskRefId || !config?.workflowConfiguration?.taskReference?.categories) {
      return null;
    }

    for (const category of config.workflowConfiguration.taskReference.categories) {
      const foundTask = category.options?.find(opt => opt.value === taskRefId);
      if (foundTask) {
        return foundTask;
      }
    }

    return null;
  };

  const getTaskRefVisibleFields = (taskRef) => (
    taskRef?.metadata?.visibleFields || taskRef?.metadata?.requiredFields || []
  );

  const getTaskRefRequiredFields = (taskRef) => (
    taskRef?.metadata?.requiredFields || []
  );


  // Handle slider value changes
  const handleSliderChange = (sliderKey, value) => {
    setMocke8ScoreData(prev => ({
      ...prev,
      [sliderKey]: value
    }));
  };

  useEffect(() => {
    if (formData.SSLaunch === '0') {
      setShowNavBar(true);
    } else {
      setShowNavBar(false);
    }
  }, [formData.SSLaunch]);

  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isMouseDevice, setIsMouseDevice] = useState(false);

  useEffect(() => {
    const hasTouch =
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      window.matchMedia('(pointer: coarse)').matches ||
      window.matchMedia('(any-pointer: coarse)').matches;

    const hasMouse = 'onmousemove' in window;

    setIsTouchDevice(hasTouch);
    setIsMouseDevice(hasMouse);
  }, []);

  // Configuration-driven field visibility
  const shouldShowField = (fieldKey, taskRefId) => {
    if (!config?.workflowConfiguration) return false;
    
    const fieldConfig = config.workflowConfiguration[fieldKey];
    if (!fieldConfig) return false;
    
    // Always visible fields
    if (fieldConfig.alwaysVisible) return true;
    
    // Task reference is always visible
    if (fieldKey === 'taskReference') return true;
    
    const selectedTaskRef = getSelectedTaskReference(taskRefId);
    
    if (!selectedTaskRef?.metadata) {
      // If no metadata, use defaultVisible from field config
      return fieldConfig.defaultVisible || false;
    }

    return getTaskRefVisibleFields(selectedTaskRef).includes(fieldKey);
  };

  // Fetch config only once on component mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setIsConfigLoading(true);
        if (!appConfig.APIBaseURL) {
          setConfig(localConfig);
          return;
        }
        const response = await api.get(`${appConfig.APIBaseURL}/api/v1/config/1`);
        setConfig(response.data);
      } catch (error) {
        console.error('Error fetching config:', error);
      } finally {
        setIsConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Initialize form data from URL params when config is loaded
  useEffect(() => {
    if (config && !isConfigLoading && !isUpdatingUrl.current) {
      const workflowConfig = config.workflowConfiguration;
      
      // Initialize form data with defaults from configuration
      const initialFormData = {};
      
      Object.keys(workflowConfig || {}).forEach((key) => {
        const fieldConfig = workflowConfig[key];
        if (fieldConfig.type === 'multiple') {
          initialFormData[key] = fieldConfig.options?.filter(opt => opt.default)?.map(opt => opt.value) || [];
        } else {
          initialFormData[key] = fieldConfig.options?.find(opt => opt.default)?.value || '';
        }
      });

      // Override with URL params if present
      let updatedFormData = { ...initialFormData };
      
      // Handle ALL fields from URL params dynamically
      Object.keys(workflowConfig || {}).forEach((key) => {
        const value = getParamValue(key);
        if (value !== undefined && value !== null) {
          if (workflowConfig[key].type === 'multiple') {
            updatedFormData[key] = value ? value.split(',') : initialFormData[key];
          } else {
            updatedFormData[key] = value || initialFormData[key];
          }
        }
      });

      // Special handling for taskRefId - it's stored in URL as taskRefId but form field is taskReference
      if (getParamValue('taskRefId')) {
        updatedFormData.taskRefId = getParamValue('taskRefId');
      }

      // Handle derived fields like SSLaunch
      if (updatedFormData.platform && workflowConfig?.platform?.options) {
        const selectedPlatform = workflowConfig.platform.options.find(
          (opt) => opt.value === updatedFormData.platform
        );
        updatedFormData.SSLaunch = selectedPlatform ? String(selectedPlatform.sslLaunch) : '0';
      }

      setFormData(updatedFormData);
      
      // No validation needed - we only check form validity when user tries to submit
    }
  }, [config, isConfigLoading, location.search]);

  const generateAuthToken = (updateUrl = true) => {
    // Generate or use existing taskInst
    const newTaskInst = taskInst || Math.floor(Date.now() / 1000).toString();

    // Update URL with all parameters if updateUrl is true
    if (updateUrl) {
      const newParams = new URLSearchParams(location.search);
      newParams.set('taskInst', newTaskInst);
      
      // Set ALL configuration fields in URL
      Object.keys(config?.workflowConfiguration || {}).forEach((key) => {
        const value = formData[key];
        if (value !== undefined) {
          if (Array.isArray(value)) {
            newParams.set(key, value.join(','));
          } else {
            newParams.set(key, value);
          }
        }
      });

      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    }

    // Build auth token - ALWAYS read from URL params first, then fallback to formData
    const mockAuthToken = {
      FHIRPATID: config?.testPatient?.fhirPatId || 'e76LFMJuUfGp.qD3SORXAHBONEyFz-.PmjfYSGGwJnjQ3',
      taskID: config?.testPatient?.taskId || '123456789',
      taskInst: newTaskInst,
      DOB: config?.testPatient?.DOB || '01/01/1900',
      FNAME: config?.testPatient?.FNAME || 'Sutter',
      LNAME: config?.testPatient?.LNAME || 'Sync',
      PATIENTSEX: config?.testPatient?.PATIENTSEX || 'Female',
      PATPREFNAME: config?.testPatient?.PATPREFNAME || 'SutterSync',
      isTest: true
    };

    // Add taskRefId from URL params or formData (this is the key field for backend)
    const taskRefIdValue = getParamValue('taskRefId') || formData.taskRefId;
    if (taskRefIdValue) {
      mockAuthToken.TASKREFID = taskRefIdValue; // Backend expects TASKREFID (uppercase)
    }

    // Add ALL configuration fields to auth token - ALWAYS read from URL params first
    Object.keys(config?.workflowConfiguration || {}).forEach((key) => {
      // Always check URL params first, then fallback to formData
      const urlValue = getParamValue(key);
      const formValue = formData[key];
      const value = (urlValue !== null && urlValue !== undefined) ? urlValue : formValue;
      
      if (value !== undefined && value !== null) {
        // Handle arrays (like enrolledPrograms)
        if (Array.isArray(value)) {
          mockAuthToken[key] = value.join(',');
        } else {
          // Add all fields directly to auth token - no special mappings
          mockAuthToken[key] = value;
        }
      }
    });

    ['vpaccPatient', 'selfEfficacy', 'dateOfBirth', 'height', 'cuffSize', 'programExtension'].forEach((key) => {
      const value = getParamValue(key);
      if (value !== undefined && value !== null && value !== '') {
        mockAuthToken[key] = value;
      }
    });

    // Add mocke8ScoreData if we have slider values
    if (Object.keys(mocke8ScoreData).length > 0) {
      mockAuthToken.mocke8ScoreData = mocke8ScoreData;
    }

    // Build the mockData payload explicitly from this test-only token — this is the
    // only place mockData should ever originate; real flow (HomePage) never builds it.
    const skipFields = ['TASKREFID', 'FHIRPATID', 'taskID', 'taskInst', 'DOB', 'FNAME', 'LNAME', 'PATIENTSEX', 'PATPREFNAME', 'isTest', 'access_token', 'refresh_token', 'WPRINTERNALID', 'E8T2DStatus', 'E8sysCCV', 'E8diaCCV', 'E8sysBASE', 'E8diaBASE'];
    const derivedMockData = {};
    Object.keys(mockAuthToken).forEach((key) => {
      if (skipFields.includes(key)) return;
      const value = mockAuthToken[key];
      if (value !== undefined && value !== null) {
        derivedMockData[key] = value;
      }
    });

    setAuthToken(mockAuthToken);
    setMockData(derivedMockData);
  };

  // Automatically generate auth token if taskInst is present and config is loaded
  useEffect(() => {
    if (taskInst && config && !authToken && !isConfigLoading) {
      generateAuthToken(false);
    }
  }, [taskInst, config, authToken, isConfigLoading, location.search]);

  const {
    loading: taskLoading,
    title,
    sections,
    isLongContent,
    error,
    errorDetails,
    readOnly,
    formData: taskFormData,
    setFormData: setTaskFormData,
    priorDecisions,
    handleNext,
    handleComplete,
    handleFinish,
    handleBack,
    handleSummary,
    handleSubmit,
    updateFormData,
    sourceContentId,
  } = useTask(authToken, undefined, mockData);

  // Task prefix used to load the matching translation bundle.
  // 404 pages come from a shared task asset regardless of selected taskRefId.
  const selectedTaskPrefix = formData.taskRefId || getParamValue('taskRefId') || '';
  const taskPrefix = title === '404-PAGE' ? '404-PAGE' : selectedTaskPrefix;

  // Load bundle when locale or task changes (English = no bundle needed)
  useEffect(() => {
    if (!locale || locale === 'en' || !taskPrefix) {
      setTranslationBundle(null);
      return;
    }
    loadTranslationBundle(locale, taskPrefix).then(setTranslationBundle);
  }, [locale, taskPrefix]);

  const { sections: displaySections, title: displayTitle } = useMemo(() => {
    if (!sections || sections.length === 0) {
      return { sections: [], title: '' };
    }
    if (!translationBundle || locale === 'en') {
      return { sections, title: title || '' };
    }
    const inferredFirstName = inferFirstNameFromEnglishContent(title || '', sections);
    const firstName = inferredFirstName || authToken?.FNAME || '';
    const lastName = authToken?.LNAME ?? '';
    const translated = applyTranslation({ title: title || '', sections }, sourceContentId, translationBundle, {
      patient_FirstName: firstName,
      patient_LastName: lastName,
      patient_PhoneNumber: authToken?.PHONE || '',
      patient_Email: authToken?.EMAIL || '',
    });
    return {
      sections: translated.sections || sections,
      title: translated.title || title || '',
    };
  }, [sections, title, sourceContentId, translationBundle, locale, authToken]);

  // Log content data when it loads and showLogs is enabled
  useEffect(() => {
    if (sections && sections.length > 0 && config?.workflowConfiguration?.page?.showLogs) {
      const contentData = {
        title,
        sections,
        isLongContent,
        readOnly,
        taskFormData,
        priorDecisions,
        timestamp: new Date().toISOString(),
        authToken: authToken ? { ...authToken, taskInst: authToken.taskInst } : null
      };
      
      

    }
  }, [sections, title, isLongContent, readOnly, taskFormData, priorDecisions, config?.workflowConfiguration?.page?.showLogs, authToken]);
  const { validateAddress } = useAddressValidation();
  const background = sections?.find((section) => section.type === 'background');
  const { imageUrl } = background || {};

  useEffect(() => {
    if (taskLoading) {
      const timer = setTimeout(() => {
        setDelayedTaskLoading(taskLoading);
      }, 1500);
      setLoadingTimer(timer);
    } else {
      if (loadingTimer) {
        clearTimeout(loadingTimer);
        setLoadingTimer(null);
      }
      setDelayedTaskLoading(false);
    }

    return () => {
      if (loadingTimer) {
        clearTimeout(loadingTimer);
      }
    };
  }, [taskLoading]);

  // Reset button states when step changes
  useEffect(() => {
    if (sections && sections.length > 0) {
      checkCurrentStepRequirements();
      setDynamicValidationState({});
    }
  }, [sections]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  
  const openModal = (content) => {
    setModalContent(content);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setModalContent(null);
    setIsModalOpen(false);
  };

  

  const handleDynamicLongContentChange = (hasLongContent) => {
    setDynamicLongContent(hasLongContent);
  };

  const handleDynamicValidationChange = (sectionId, isValid) => {
    setDynamicValidationState(prev => {
      const newState = {
        ...prev,
        [sectionId]: isValid
      };
      return newState;
    });
  };

  const handleButtonAction = async (action, buttonData) => {
    if (window.actionInProgress) {
      return;
    }
  
    try {
      window.actionInProgress = true;
      const { actionType, metadata, clearKeys, modalContent, url, deeplinkUrl = null } = buttonData;
      // Check if this is a "No Thanks" button and clear form fields
      const isNoThanksButton = buttonData?.label?.toLowerCase().includes('no thanks');
      if (isNoThanksButton) {
        // Clear only the remaining_eligible_programs field (current page selections)
        // Preserve other fields that might contain data from previous pages
        setTaskFormData(prevFormData => {
          const clearedFormData = { ...prevFormData };
          
          // Only clear the remaining_eligible_programs field
          if (clearedFormData.remaining_eligible_programs) {
            delete clearedFormData.remaining_eligible_programs;
          }
          
          return clearedFormData;
        });
      }
      
      switch (action) {
        case 'modal':
          if (actionType === 'close') {
            closeModal();
            setTimeout(() => {
              window.close();
            }, 2000);
          } else {
            openModal(modalContent);
          }
          break;
        case 'next':
          let params;
          if (metadata) {
            params = { actionType, metadata, clearKeys };
          } else if (actionType) {
            params = { actionType, clearKeys };
          } else {
            params = {};
          }
          await handleNext(params);
          break;
        case 'finish':
          let paramsFinish;
          if (actionType) {
            paramsFinish = { actionType };
          } else {
            paramsFinish = {}; 
          }
          await handleFinish(paramsFinish);
          break;
        case 'back':
          setDynamicValidationState({}); // Clear dynamic validation state when going back
          let backParams;
          if (metadata) {
            backParams = { actionType, metadata, clearKeys };
          } else if (actionType) {
            backParams = { actionType, clearKeys };
          } else {
            backParams = {};
          }
          await handleBack(backParams);

          // Apply local clear only after server-driven back navigation succeeds.
          if (String(actionType || '').trim().toLowerCase() === 'clear' && Array.isArray(clearKeys) && clearKeys.length > 0) {
            setTaskFormData(prevFormData => {
              const clearedFormData = { ...prevFormData };
              clearKeys.forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(clearedFormData, key)) {
                  delete clearedFormData[key];
                }
              });
              return clearedFormData;
            });
          }
          break;
        case 'summary':
          setTaskFormData({}); // Clear form data when going to summary
          setDynamicValidationState({}); // Clear dynamic validation state when going to summary
          await handleSummary({});
          break;
        case 'complete':
          if (actionType === 'onboarding') {
            await handleComplete({ actionType });
          } else if (actionType === 'enrollment') {
            await handleComplete({ actionType, metadata });
          } else {
            await handleComplete({});
          }
          break;
        case 'link':
          if (url || deeplinkUrl) {
            const linkToOpen = !showNavBar ? deeplinkUrl : url;
            window.location.href = linkToOpen;
          } else {
            console.error("URL or deeplinkUrl is required for 'openLink' action.");
          }
          break;
        case 'close':
          setTimeout(() => {
            window.close();
          }, 2000);
          break;
        case 'submit':
          if (taskFormData || metadata) {
            if (actionType === 'shipping') {
              const getFieldValue = (field, metaField = field) =>
                taskFormData?.[field]?.value || metadata?.[metaField] || '';
            
              const shippingData = {
                firstName: getFieldValue('firstName'),
                lastName: getFieldValue('lastName'),
                email: getFieldValue('emailAddress', 'email'),
                phoneNumber: getFieldValue('phoneNumber'),
                preferredLanguage: 'en_US',
                street: getFieldValue('street'),
                addressLine2: getFieldValue('addressLine2'),
                city: getFieldValue('city'),
                zipCode: getFieldValue('zipCode'),
                state: getFieldValue('state'),
                country: 'US',
              };
            
              const formMetadata = { ...metadata, ...shippingData };
            
              const { isValid, error } = await validateAddress(shippingData);
            
              if (!isValid) {
                console.error('Address validation failed:', error);
                openModal({
                  title: 'Invalid Address',
                  content: [{"type":"paragraph","content":"It appears the address you've entered is invalid. Please double check your address and try again.","variant":"body-sm-min-reg"}],
                  actionLabel: 'Close',
                  onAction: closeModal,
                });
                return;
              }
            
              await handleNext({ actionType, metadata: formMetadata });
            } else if (actionType === 'ranking') {
              const getFieldValueArray = (field) =>
                Array.isArray(taskFormData?.[field]?.value) ? taskFormData[field].value : [];
        
              const rankingMetadata = {
                content_ranking: {
                  feedback: getFieldValueArray('tell_us_why'),
                  value: taskFormData?.['how_was_it']?.value || '',
                  comments: taskFormData?.['hear_more_comments']?.value || ''
                }
              };
        
              await handleSubmit({ actionType, metadata: rankingMetadata });
            } else {
              const submitParams = {};
              if (actionType) {
                submitParams.actionType = actionType;
              }
              if (metadata) {
                submitParams.metadata = metadata;
              }
              if (clearKeys) {
                submitParams.clearKeys = clearKeys;
              }
              await handleSubmit(submitParams);
            }
          } else {
            console.warn('No form data or metadata available for submission.');
          }
          break;
        default:
          console.warn(`Unhandled action type: ${action}`);
      }
      document.activeElement.blur();
    } catch (error) {
      console.error(`Error handling action "${action}":`, error);
    } finally {
      window.actionInProgress = false;
    }
  };

  // Glassmorphic styling
  const glassBg = 'rgba(255, 255, 255, 0.1)';
  const glassBorder = 'rgba(255, 255, 255, 0.2)';
  const glassShadow = '0 8px 32px 0 rgba(31, 38, 135, 0.37)';
  const glassBackdrop = 'blur(4px)';
  const dropdownBg = 'rgba(255, 255, 255, 0.9)';
  const dropdownBorder = 'rgba(139, 92, 246, 0.3)';
  const hoverBg = 'rgba(139, 92, 246, 0.1)';
  const focusBorder = 'rgba(139, 92, 246, 0.5)';
  const buttonBg = 'rgba(255, 255, 255, 0.8)';
  const selectedButtonBg = 'var(--sutter-sync-purple)';
  const selectedButtonColor = 'white';

  // Render form field based on configuration
  const renderFormField = (fieldKey, fieldConfig) => {
    const selectedTaskRef = getSelectedTaskReference(formData.taskRefId);

    const isRequired = shouldShowField(fieldKey, formData.taskRefId) && 
      (fieldConfig.required || getTaskRefRequiredFields(selectedTaskRef).includes(fieldKey));

        if (fieldKey === 'taskReference') {
      // Check if we have the new categorized structure or old flat structure
             if (fieldConfig.categories && fieldConfig.categories.length > 0) {
         return (
           <FormControl key={fieldKey} isRequired>
             <FormLabel fontSize={['sm', 'md']} color="gray.700" fontWeight="medium" fontFamily="Sora, sans-serif">
               {fieldConfig.label}
             </FormLabel>
            <SearchableTaskDropdown
              value={formData.taskRefId || params.get('taskRefId') || ''}
              onChange={(value) => handleInputChange('taskRefId', value)}
              categories={fieldConfig.categories}
              placeholder="Search tasks by name, category, or description..."
              isRequired={true}
            />
            {formData.taskRefId && (
              <Button
                mt={2}
                size="sm"
                variant="outline"
                colorScheme="purple"
                fontFamily="Sora, sans-serif"
                borderColor={dropdownBorder}
                onClick={handleOpenCloneModal}
              >
                Clone Task
              </Button>
            )}
          </FormControl>
        );
             } else {
         // Fallback to old flat structure
         return (
           <FormControl key={fieldKey} isRequired>
             <FormLabel fontSize={['sm', 'md']} color="gray.700" fontWeight="medium" fontFamily="Sora, sans-serif">
               {fieldConfig.label}
             </FormLabel>
            <Select
              name="taskRefId"
              value={formData.taskRefId}
              onChange={(e) => handleInputChange('taskRefId', e.target.value)}
              placeholder="Select Task Reference"
              size="lg"
              bg={dropdownBg}
              borderColor={dropdownBorder}
              _hover={{ 
                borderColor: focusBorder, 
                bg: hoverBg,
                transform: 'translateY(-1px)',
                boxShadow: glassShadow
              }}
              _focus={{ 
                borderColor: focusBorder, 
                boxShadow: `0 0 0 1px ${focusBorder}, ${glassShadow}`,
                bg: dropdownBg
              }}
              borderRadius="lg"
              transition="all 0.3s ease"
              backdropFilter={glassBackdrop}
              boxShadow={glassShadow}
            >
             {fieldConfig.options?.map((option) => (
               <option key={option.value} value={option.value}>
                 {option.label}
               </option>
             ))}
            </Select>
          </FormControl>
        );
      }
    }

    // Handle single select fields with toggle buttons for 2-3 options, dropdown for more
    if (fieldConfig.type === 'single' && fieldConfig.options && fieldConfig.options.length > 0) {
      // Use toggle buttons for 2-3 options, dropdown for more
             if (fieldConfig.options.length <= 3) {
         return (
           <FormControl key={fieldKey} isRequired={isRequired}>
             <FormLabel fontSize={['sm', 'md']} color="gray.700" fontWeight="medium" fontFamily="Sora, sans-serif">
               {fieldConfig.label}
             </FormLabel>
            <ButtonGroup
              isAttached
              flexDirection={['column', 'row']}
              justifyContent="center"
              spacing={[0, 0]}
              width="100%"
            >
              {fieldConfig.options.map((option, index) => (
                <Button
                  key={option.value}
                  onClick={() => {
                    const isSelected = formData[fieldKey] === option.value;
                    handleInputChange(fieldKey, isSelected ? '' : option.value);
                  }}
                  bg={formData[fieldKey] === option.value ? selectedButtonBg : buttonBg}
                  color={formData[fieldKey] === option.value ? 'white' : 'gray.700'}
                  borderRadius={
                    index === 0
                      ? ['lg', 'lg 0 0 lg']
                      : index === fieldConfig.options.length - 1
                      ? ['lg', '0 lg lg 0']
                      : ['lg', '0']
                  }
                  _hover={{ 
                    bg: formData[fieldKey] === option.value ? 'var(--sutter-sync-purple-700)' : hoverBg,
                    transform: 'translateY(-1px)',
                    boxShadow: glassShadow
                  }}
                  flex={['none', '1']}
                  size="lg"
                  border="1px solid"
                  borderColor={formData[fieldKey] === option.value ? focusBorder : dropdownBorder}
                  marginBottom={[2, 0]}
                  backdropFilter={glassBackdrop}
                  transition="all 0.3s ease"
                >
                  {option.label}
                </Button>
              ))}
            </ButtonGroup>
          </FormControl>
        );
             } else {
         // Use dropdown for more than 3 options
         return (
           <FormControl key={fieldKey} isRequired={isRequired}>
             <FormLabel fontSize={['sm', 'md']} color="gray.700" fontWeight="medium" fontFamily="Sora, sans-serif">
               {fieldConfig.label}
             </FormLabel>
                         <Select
               name={fieldKey}
               value={formData[fieldKey] || ''}
               onChange={(e) => handleInputChange(fieldKey, e.target.value)}
               placeholder={`Select ${fieldConfig.label}`}
               size="lg"
               bg={dropdownBg}
               borderColor={dropdownBorder}
               _hover={{ 
                 borderColor: focusBorder, 
                 bg: hoverBg,
                 transform: 'translateY(-1px)',
                 boxShadow: glassShadow
               }}
               _focus={{ 
                 borderColor: focusBorder, 
                 boxShadow: `0 0 0 1px ${focusBorder}, ${glassShadow}`,
                 bg: dropdownBg
               }}
               borderRadius="lg"
               transition="all 0.3s ease"
               backdropFilter={glassBackdrop}
               boxShadow={glassShadow}
             >
              {fieldConfig.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
        );
      }
    }

        // Handle input fields (like programExtension)
         if (fieldConfig.type === 'input' || (fieldConfig.type === 'single' && (!fieldConfig.options || fieldConfig.options.length === 0))) {
       return (
         <FormControl key={fieldKey} isRequired={isRequired}>
           <FormLabel fontSize={['sm', 'md']} color="gray.700" fontWeight="medium" fontFamily="Sora, sans-serif">
             {fieldConfig.label}
           </FormLabel>
                      <Input
             name={fieldKey}
             value={formData[fieldKey] || ''}
             onChange={(e) => handleInputChange(fieldKey, e.target.value)}
             placeholder={fieldConfig.placeholder || `Enter ${fieldConfig.label}`}
             size="lg"
             bg={dropdownBg}
             borderColor={dropdownBorder}
             _hover={{ 
               bg: hoverBg,
               borderColor: focusBorder,
               transform: 'translateY(-1px)',
               boxShadow: glassShadow
               }}
             _focus={{ 
               borderColor: focusBorder, 
               boxShadow: `0 0 0 1px ${focusBorder}, ${glassShadow}`,
               bg: dropdownBg
             }}
             borderRadius="lg"
             transition="all 0.3s ease"
             backdropFilter={glassBackdrop}
             boxShadow={glassShadow}
           />
        </FormControl>
      );
    }

    // Handle slider fields (like mocke8ScoreData)
    if (fieldConfig.type === 'slider' && fieldConfig.e8Sliders) {
      return (
        <FormControl key={fieldKey} isRequired={isRequired}>
          <FormLabel fontSize={['sm', 'md']} color="gray.700" fontWeight="medium" fontFamily="Sora, sans-serif">
            {fieldConfig.label}
          </FormLabel>
          
          {/* Single master container */}
          <Box 
            p={6} 
            bg="white" 
            borderRadius="xl" 
            border="1px solid" 
            borderColor="gray.200" 
            shadow="sm"
            _hover={{ shadow: "md" }}
            transition="all 0.2s ease"
          >
            {/* Header */}
            <Flex justify="space-between" align="center" mb={6}>
              <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                E8 Score Configuration
              </Text>
              <Text fontSize="sm" color="gray.500">
                {fieldConfig.e8Sliders.length} metrics
              </Text>
            </Flex>

            {/* Sliders */}
            <VStack spacing={5} align="stretch">
              {fieldConfig.e8Sliders.map((slider, index) => (
                <Box key={slider.key}>
                  {/* Slider header with label and value */}
                  <Flex justify="space-between" align="center" mb={3}>
                    <Text fontSize="md" fontWeight="medium" color="gray.700">
                      {slider.label}
                    </Text>
                    <Box
                      bg="var(--sutter-sync-purple)"
                      color="white"
                      px={4}
                      py={1}
                      borderRadius="full"
                      fontSize="sm"
                      fontWeight="bold"
                      minW="50px"
                      textAlign="center"
                      boxShadow="sm"
                    >
                      {mocke8ScoreData[slider.key] ?? slider.defaultValue ?? 0}
                    </Box>
                  </Flex>
                  
                  {/* Modern slider */}
                  <Box px={2}>
                    <Slider
                      value={mocke8ScoreData[slider.key] ?? slider.defaultValue ?? 0}
                      onChange={(value) => handleSliderChange(slider.key, value)}
                      min={slider.min ?? 0}
                      max={slider.max ?? 100}
                      step={1}
                      colorScheme="purple"
                      size="lg"
                    >
                      <SliderTrack 
                        bg="gray.100" 
                        h="6px" 
                        borderRadius="full"
                        _hover={{ bg: "gray.200" }}
                        transition="all 0.2s ease"
                      >
                        <SliderFilledTrack 
                          bg="var(--sutter-sync-purple)"
                          borderRadius="full"
                        />
                      </SliderTrack>
                      <SliderThumb 
                        bg="var(--sutter-sync-purple)" 
                        border="3px solid white" 
                        shadow="lg"
                        w="24px" 
                        h="24px"
                        _hover={{ 
                          bg: "var(--sutter-sync-purple-700)",
                          transform: "scale(1.1)"
                        }}
                        _active={{ 
                          bg: "var(--sutter-sync-purple-700)",
                          transform: "scale(1.05)"
                        }}
                        transition="all 0.2s ease"
                      />
                    </Slider>
                    
                    {/* Min/Max labels */}
                    <Flex justify="space-between" mt={2} px={1}>
                      <Text fontSize="xs" color="gray.400" fontWeight="medium">
                        {slider.min ?? 0}
                      </Text>
                      <Text fontSize="xs" color="gray.400" fontWeight="medium">
                        {slider.max ?? 100}
                      </Text>
                    </Flex>
                  </Box>
                </Box>
              ))}
            </VStack>

            {/* Footer */}
            <Box mt={6} pt={4} borderTop="1px solid" borderColor="gray.100">
              <Text fontSize="xs" color="gray.500" textAlign="center">
                Configure E8 Score values for testing. These will be passed as mock data to the backend.
              </Text>
            </Box>
          </Box>
        </FormControl>
      );
    }

             if (fieldConfig.type === 'multiple') {
       return (
         <FormControl key={fieldKey} isRequired={isRequired}>
           <FormLabel fontSize={['sm', 'md']} color="gray.700" fontWeight="medium" fontFamily="Sora, sans-serif">
             {fieldConfig.label}
           </FormLabel>
          <SimpleGrid columns={[1, 2, 3]} spacing={4}>
            {fieldConfig.options.map((option) => {
              const isSelected = formData[fieldKey]?.includes(option.value);
              return (
                                 <Card
                   key={option.value}
                   onClick={() => {
                     const currentValues = formData[fieldKey] || [];
                     const newValues = isSelected
                       ? currentValues.filter((val) => val !== option.value)
                       : [...currentValues, option.value];
                     handleInputChange(fieldKey, newValues);
                   }}
                   cursor="pointer"
                   bg={isSelected ? 'rgba(139, 92, 246, 0.15)' : dropdownBg}
                   borderColor={isSelected ? focusBorder : dropdownBorder}
                   borderWidth={isSelected ? '2px' : '1px'}
                   _hover={{ 
                     bg: hoverBg, 
                     borderColor: focusBorder,
                     transform: 'translateY(-2px)',
                     boxShadow: glassShadow
                   }}
                   borderRadius="lg"
                   transition="all 0.3s ease"
                   boxShadow={isSelected ? `0 0 0 1px ${focusBorder}, ${glassShadow}` : glassShadow}
                   backdropFilter={glassBackdrop}
                 >
                   <CardBody p={4}>
                     <Text
                       fontFamily="var(--body-lg-min-reg-font-family)"
                       fontSize="var(--body-lg-min-reg-font-size)"
                       fontWeight="var(--body-lg-min-reg-font-weight)"
                       lineHeight="var(--body-lg-min-reg-line-height)"
                       color={isSelected ? 'var(--sutter-sync-purple-700)' : 'gray.700'}
                       textAlign="center"
                     >
                       {option.label}
                     </Text>
                   </CardBody>
                 </Card>
               );
             })}
           </SimpleGrid>
         </FormControl>
       );
     }

         return (
       <FormControl key={fieldKey} isRequired={isRequired}>
         <FormLabel fontSize={['sm', 'md']} color="gray.700" fontWeight="medium" fontFamily="Sora, sans-serif">
           {fieldConfig.label}
         </FormLabel>
        <ButtonGroup
          isAttached
          flexDirection={['column', 'row']}
          justifyContent="center"
          spacing={[0, 0]}
          width="100%"
        >
          {fieldConfig.options.map((option, index) => (
            <Button
              key={option.value}
              onClick={() => {
                const isSelected = formData[fieldKey] === option.value;
                handleInputChange(fieldKey, isSelected ? '' : option.value);
              }}
              bg={formData[fieldKey] === option.value ? selectedButtonBg : buttonBg}
              color={formData[fieldKey] === option.value ? 'white' : 'gray.700'}
              borderRadius={
                index === 0
                  ? ['lg', 'lg 0 0 lg']
                  : index === fieldConfig.options.length - 1
                  ? ['lg', '0 lg lg 0']
                  : ['lg', '0']
              }
              _hover={{ 
                bg: formData[fieldKey] === option.value ? 'var(--sutter-sync-purple-700)' : hoverBg,
                transform: 'translateY(-1px)',
                boxShadow: glassShadow
              }}
              flex={['none', '1']}
              size="lg"
              border="1px solid"
              borderColor={formData[fieldKey] === option.value ? focusBorder : dropdownBorder}
              marginBottom={[2, 0]}
              backdropFilter={glassBackdrop}
              transition="all 0.3s ease"
            >
              {option.label}
            </Button>
          ))}
        </ButtonGroup>
      </FormControl>
    );
  };

  return (
    <>
      <PageViewTracker pageName={title} />
      {showNavBar && <NavBarComponent />}
             <Flex direction="column" minHeight="100vh" bg={['white', 'gray.100']} mt={showNavBar ? 6 : 0} px={[4, 6]} fontFamily="Roboto, sans-serif">
        {!authToken && !taskInst ? (
          <Box 
            width={['100%', '600px']} 
            mx="auto" 
            mt={showNavBar ? 20 : 4} 
            p={8} 
            bg="white" 
            border="1px solid" 
            borderColor="gray.200" 
            borderRadius="lg" 
            shadow="lg"
            transition="all 0.3s ease"
          >
            {isConfigLoading ? (
              <VStack spacing={5} align="stretch">
                {/* Beating Heart Loading Animation */}
                <Flex justifyContent="center" mb={4}>
                  <Image src={loadingHeart} alt="Loading..." boxSize="50px" />
                </Flex>
                <Skeleton height="40px" borderRadius="md" />
                <SkeletonText noOfLines={3} spacing="4" skeletonHeight="4" />
                <Skeleton height="48px" borderRadius="md" />
                <Skeleton height="48px" borderRadius="md" />
                <Skeleton height="48px" borderRadius="md" />
                <Skeleton height="48px" borderRadius="md" />
              </VStack>
            ) : (
              <>
                                 <Heading 
                   size="md" 
                   mb={4} 
                   textAlign="center" 
                   color="gray.700"
                   fontWeight="bold"
                   fontFamily="Sora, sans-serif"
                 >
                   {config?.workflowConfiguration?.page?.title}
                 </Heading>
                                 <Text 
                   fontSize="sm" 
                   fontWeight="normal" 
                   mb={6} 
                   color="gray.600"
                   lineHeight="1.6"
                   fontFamily="Roboto, sans-serif"
                 >
                   {config?.workflowConfiguration?.page?.description}
                 </Text>
                                 <VStack spacing={5} align="stretch">
                   
                   
                   {config?.workflowConfiguration && Object.keys(config.workflowConfiguration).map((fieldKey) => {
                     const fieldConfig = config.workflowConfiguration[fieldKey];
                     
                     // Only render fields that should be visible
                     if (!shouldShowField(fieldKey, formData.taskRefId)) {
                       return null;
                     }
                     
                     return renderFormField(fieldKey, fieldConfig);
                   })}

                                                         <Button
                      onClick={() => generateAuthToken(true)}
                      size="lg"
                      width="full"
                      mt={6}
                      bg="var(--sutter-sync-purple)"
                      color="white"
                      _hover={{ bg: 'var(--sutter-sync-purple-700)', transform: 'translateY(-2px)' }}
                      _active={{ bg: 'var(--sutter-sync-purple-700)' }}
                      transition="all 0.2s ease"
                      boxShadow="md"
                      isDisabled={!isFormValid(formData, mocke8ScoreData)}
                    >
                      Get Started
                    </Button>
                </VStack>
              </>
            )}
          </Box>
        ) : (
          <Flex
            justify="center"
            align="center"
            height="100%"
            bg={readOnly ? "white" : ['white', 'gray.100']}
            py={showNavBar ? 14 : 0}
          >
            {delayedTaskLoading ? (
              <Box
                flex="1"
                display="flex"
                flexDirection="column"
                overflowY="auto"
                overflowX="hidden"
                maxWidth="390px"
                height={(isLongContent || dynamicLongContent) ? '100%' : '708px'}
                bg="white"
                borderRadius={['none', 'md']}
                shadow={['none', '0px 4px 32px 0px #00000026']}
                py={8}
                px={4}
                minHeight="708px"
              >
                <Skeleton height="40px" width="60%" mb={6} borderRadius="md" />
                <SkeletonText noOfLines={4} spacing="4" skeletonHeight="4" mb={6} />
                <Skeleton height="48px" borderRadius="md" mb={4} />
                <Skeleton height="48px" borderRadius="md" mb={4} />
                <Skeleton height="48px" borderRadius="md" mb={4} />
                <Skeleton height="60px" borderRadius="md" />
              </Box>
            ) : readOnly ? (
              <Box
                bg="white"
                width={['100%', '390px']}
                height='100%'
                display="flex"
                flexDirection="column"
                padding={2}
                mb={4}
                mt={showNavBar ? 6 : 0}
              >
                {displaySections.map((section, sectionIndex) => {
                  if (readOnly && section.type === 'progress') {
                    return null;
                  }
                  return (
                    <ComponentFactory
                      key={section.id || `section-${sectionIndex}`}
                      section={section}
                      theme="light"
                      priorDecisions={priorDecisions}
                      formData={taskFormData}
                      readOnly={readOnly}
                      updateFormData={updateFormData}
                      handleButtonAction={handleButtonAction}
                      onLongContentChange={handleDynamicLongContentChange}
                      onDynamicValidationChange={handleDynamicValidationChange}
                    />
                  );
                })}
              </Box>
            ) : (
              <Flex justify="center" align="center" width="100%">
                <Box
                  flex="1"
                  display="flex"
                  flexDirection="column"
                  overflowY="auto"
                  overflowX="hidden"
                  maxWidth='390px'
                  height={(isLongContent || dynamicLongContent) ? '100%' : '708px'}
                  bg={sections && sections.length > 0 ? 'white' : 'transparent'}
                  borderRadius={sections && sections.length > 0 ? ['none', 'md'] : 'none'}
                  shadow={sections && sections.length > 0 ? ['none', '0px 4px 32px 0px #00000026'] : 'none'}
                  py={8}
                  px={4}
                  minHeight="708px"
                  backgroundImage={imageUrl ? `url(${appConfig.ImageBlobBaseUrl}/${imageUrl})` : 'none'}
                  backgroundSize="cover"
                  backgroundRepeat="no-repeat"
                  backgroundPosition="top left"
                >
                  {error ? (
                    <Box textAlign="center" color="red.500" mt={4}>
                      <Text fontSize="lg" fontWeight="bold">
                        Something's wrong on our end — looks like we may need a check up. Try again later, and give us a call if it keeps happening. 
                      </Text>
                      <Text fontSize="sm">{error}</Text>
                      {isErrorDetailsEnabled() && errorDetails && (
                        <Box
                          mt={4}
                          textAlign="left"
                          bg="gray.800"
                          color="gray.100"
                          borderRadius="lg"
                          border="1px solid"
                          borderColor="gray.600"
                          p={4}
                        >
                          <Flex justify="space-between" align="center" mb={3}>
                            <Text fontSize="sm" fontWeight="bold" color="orange.300">
                              Technical Details
                            </Text>
                            <Button
                              size="xs"
                              variant="outline"
                              colorScheme="orange"
                              onClick={() => {
                                navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
                                setErrorDumpCopied(true);
                                setTimeout(() => setErrorDumpCopied(false), 2000);
                              }}
                            >
                              {errorDumpCopied ? 'Copied!' : 'Copy for support'}
                            </Button>
                          </Flex>
                          <VStack align="stretch" spacing={1.5} fontSize="sm" mb={3}>
                            <Flex><Text fontWeight="semibold" minWidth="110px">Status</Text><Text>{errorDetails.status ? `${errorDetails.status} ${errorDetails.statusText || ''}`.trim() : (errorDetails.isNetworkError ? 'Network error (no response)' : '—')}</Text></Flex>
                            <Flex><Text fontWeight="semibold" minWidth="110px">Error code</Text><Text fontFamily="mono">{errorDetails.errorCode || errorDetails.errorName || '—'}</Text></Flex>
                            <Flex><Text fontWeight="semibold" minWidth="110px">Message</Text><Text>{errorDetails.message || '—'}</Text></Flex>
                            <Flex><Text fontWeight="semibold" minWidth="110px">Operation</Text><Text>{errorDetails.operation || '—'}</Text></Flex>
                            <Flex><Text fontWeight="semibold" minWidth="110px">Request ID</Text><Text fontFamily="mono" fontSize="xs">{errorDetails.requestId || '—'}</Text></Flex>
                            <Flex><Text fontWeight="semibold" minWidth="110px">Time</Text><Text>{errorDetails.timestamp || '—'}</Text></Flex>
                          </VStack>
                          <Accordion allowToggle>
                            <AccordionItem border="none">
                              <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                                <Text fontSize="xs" color="gray.400" flex="1" textAlign="left">
                                  Show full technical dump
                                </Text>
                                <AccordionIcon />
                              </AccordionButton>
                              <AccordionPanel px={0} maxHeight="320px" overflowY="auto">
                                <Text as="pre" fontSize="xs" whiteSpace="pre-wrap" wordBreak="break-word">
                                  {JSON.stringify(errorDetails, null, 2)}
                                </Text>
                              </AccordionPanel>
                            </AccordionItem>
                          </Accordion>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    displaySections.map((section, sectionIndex) => {
                      if (!section.type) {
                        console.error(
                          `Missing type for item at Section ${sectionIndex}:`,
                          section
                        );
                        return (
                          <Text
                            key={`${sectionIndex}`}
                            color="red.500"
                            fontSize="sm"
                          >
                            Invalid item structure
                          </Text>
                        );
                      }
                      if (readOnly && section.type === 'progress') {
                        return null;
                      }
                      if (!readOnly && section.type === 'button' && Array.isArray(section.cta)) {
                        const hasHalfVariant = section.cta?.some(ctaItem => ctaItem?.variant?.includes('-half'));
                        const Wrapper = hasHalfVariant ? Flex : Box;
                        const wrapperProps = hasHalfVariant ? { justify: "space-between", gap: 4, width: "100%" } : {};
                        
                        // SIMPLIFIED: Only disable buttons when we actually have validation failures
                        const hasValidationFailures = Object.keys(dynamicValidationState).length > 0 && 
                          Object.values(dynamicValidationState).some(isValid => !isValid);
                        
                        return (
                          <Wrapper key="btns-wrapper" {...wrapperProps}>
                            {section.cta.map((ctaItem, btnIndex) => (
                              <ButtonComponent
                                key={`cta-${btnIndex}`}
                                action={ctaItem?.action}
                                label={ctaItem?.label}
                                ariaLabel={ctaItem?.ariaLabel}
                                tooltip={ctaItem?.tooltip}
                                variant={ctaItem?.variant || 'solid'}
                                marginBottom={ctaItem?.marginBottom}
                                icons={ctaItem?.icons}
                                onClick={() => handleButtonAction(ctaItem?.action, ctaItem)}
                                textDecoration={ctaItem?.textDecoration}
                                isTouchDevice={isTouchDevice}
                                isMouseDevice={isMouseDevice}
                                isDisabled={
                                  // ONLY disable next/submit buttons when we have actual validation failures
                                  // Exception: "No Thanks" buttons should always be enabled (optional actions)
                                  (ctaItem?.action === 'next' || ctaItem?.action === 'submit') 
                                    ? hasValidationFailures && !(ctaItem?.label?.toLowerCase().includes('no thanks'))
                                    : false
                                }
                              />
                            ))}
                          </Wrapper>
                        );
                      }               
                      return (
                        <ComponentFactory
                          key={section.id || `section-${sectionIndex}`}
                          section={section}
                          theme="light"
                          priorDecisions={priorDecisions}
                          formData={taskFormData}
                          readOnly={readOnly}
                          updateFormData={updateFormData}
                          handleButtonAction={handleButtonAction}
                          onLongContentChange={handleDynamicLongContentChange}
                          onDynamicValidationChange={handleDynamicValidationChange}
                        />
                      );
                    })
                  )}
                </Box>
              </Flex>
            )}
          </Flex>
        )}
        <ModalComponent
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          modalContent={modalContent}
          handleButtonAction={handleButtonAction}
        />
        

         {/* Development/Admin Links */}
        <Box
          position="fixed"
          bottom={4}
          right={4}
          bg="white"
          p={3}
          borderRadius="md"
          shadow="lg"
          border="1px solid"
          borderColor="gray.200"
          zIndex={1000}
        >
          <VStack spacing={2} align="stretch">
            <Text fontSize="xs" fontWeight="bold" color="gray.600">Admin</Text>
            {/*}<Link to="/workflow-config">
              <Button size="sm" colorScheme="blue" variant="outline">
                Workflow Config
              </Button>
            </Link>*/}
            <Button 
              size="sm" 
              colorScheme="green" 
              variant="outline"
              onClick={() => window.location.href = '/content'}
            >
              Content Engine
            </Button>

            {/* Language selector */}
            <Text fontSize="xs" fontWeight="bold" color="gray.600" mt={2}>Language</Text>
            {SUPPORTED_LOCALES.map(loc => (
              <Button
                key={loc.code}
                size="sm"
                colorScheme={locale === loc.code ? 'blue' : 'gray'}
                variant={locale === loc.code ? 'solid' : 'outline'}
                onClick={() => setLanguage(loc.code)}
              >
                {loc.label}
              </Button>
            ))}

          </VStack>
        </Box>
      </Flex>

      {/* ── Clone Task Modal ────────────────────────────────────────────── */}
      <Modal isOpen={isCloneOpen} onClose={onCloneClose} isCentered size="md">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" fontFamily="Sora, sans-serif">
          <ModalHeader fontFamily="Sora, sans-serif" borderBottomWidth="1px">Clone Task</ModalHeader>
          <ModalCloseButton isDisabled={cloneLoading} />
          <ModalBody py={5}>
            <VStack spacing={4} align="stretch">

              {cloneResult && (
                <Alert status={cloneResult.success ? 'success' : 'error'} borderRadius="lg">
                  <AlertIcon />
                  <AlertDescription fontFamily="Sora, sans-serif" fontSize="sm">
                    {cloneResult.success
                      ? `Task cloned successfully. Switching to ${cloneNewTaskCode.trim()}…`
                      : cloneResult.error}
                  </AlertDescription>
                </Alert>
              )}

              <FormControl>
                <FormLabel fontSize="sm" color="gray.700" fontFamily="Sora, sans-serif">Source Task Code</FormLabel>
                <Input
                  value={formData.taskRefId || ''}
                  isReadOnly
                  bg="gray.50"
                  borderRadius="lg"
                  fontFamily="Sora, sans-serif"
                  fontSize="sm"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" color="gray.700" fontFamily="Sora, sans-serif">Source Revision</FormLabel>
                <Input
                  value={cloneRevision}
                  onChange={(e) => setCloneRevision(e.target.value)}
                  placeholder="Latest"
                  borderRadius="lg"
                  fontFamily="Sora, sans-serif"
                  fontSize="sm"
                  isDisabled={cloneLoading}
                  borderColor="rgba(139, 92, 246, 0.3)"
                  _focus={{ borderColor: 'rgba(139, 92, 246, 0.5)', boxShadow: '0 0 0 1px rgba(139, 92, 246, 0.5)' }}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" color="gray.700" fontFamily="Sora, sans-serif">New Task Code</FormLabel>
                <Input
                  value={cloneNewTaskCode}
                  onChange={(e) => setCloneNewTaskCode(e.target.value)}
                  placeholder="e.g. HTN-ONBOARDING-V2"
                  borderRadius="lg"
                  fontFamily="Sora, sans-serif"
                  fontSize="sm"
                  isDisabled={cloneLoading}
                  borderColor="rgba(139, 92, 246, 0.3)"
                  _focus={{ borderColor: 'rgba(139, 92, 246, 0.5)', boxShadow: '0 0 0 1px rgba(139, 92, 246, 0.5)' }}
                />
              </FormControl>

              <Checkbox
                isChecked={cloneUpdateConfig}
                onChange={(e) => setCloneUpdateConfig(e.target.checked)}
                isDisabled={cloneLoading}
                colorScheme="purple"
                fontFamily="Sora, sans-serif"
                fontSize="sm"
              >
                <Text fontSize="sm" fontFamily="Sora, sans-serif" color="gray.700">
                  Also update Customer.Config?
                </Text>
              </Checkbox>

            </VStack>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" gap={2}>
            <Button
              variant="ghost"
              onClick={onCloneClose}
              isDisabled={cloneLoading}
              fontFamily="Sora, sans-serif"
              fontSize="sm"
            >
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleCloneTask}
              isDisabled={cloneLoading || !cloneNewTaskCode.trim() || !!cloneResult?.success}
              fontFamily="Sora, sans-serif"
              fontSize="sm"
            >
              {cloneLoading ? <Spinner size="sm" /> : 'Clone'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ContentPage;
